import { ActivityParticipant } from './Room';
import type { ParticipantPuppet } from './ParticipantPuppet';
import type { HostExperienceMode } from './HostPanel';
import type { SixyncEvent, SixyncSnapshot } from './Sixync';

export interface SocketUser extends ActivityParticipant {
    socketId: string;
    /** Server-issued room join identity; visual evidence cannot survive leave/rejoin. */
    visionMembershipId?: string;
    /**
     * #3837 — the companion avatar this human is wearing, or null/absent for
     * a video tile. Lives on the room user record so `EXISTING_USERS` and
     * `NEW_USER` carry it to late joiners with no extra sync path.
     */
    puppet?: ParticipantPuppet | null;
}

export interface RoomContextUpdatePayload {
    roomId: string;
    searchQuery: string | null;
    searchResults: string | null;
    searchSources: Array<{
        source_title: string;
        source_summary: string;
        source_url: string;
        relevance: number;
        confidence: number;
    }>;
    searchImages?: Array<{
        url: string;
        thumbnailUrl?: string;
        description?: string;
        sourceUrl?: string;
        sourceTitle?: string;
    }>;
    searchVideos?: Array<{
        url: string;
        title: string;
        description?: string;
        thumbnailUrl?: string;
        duration?: string;
        channel?: string;
    }>;
    scrapedContent?: Array<{
        url: string;
        title?: string;
        content: string;
        success: boolean;
    }>;
    /** Indicates that enrichment (images, videos, scraping) is still in progress */
    isEnriching?: boolean;
    /**
     * #4401 — the turn's prediction-market odds, for the standard-room
     * search-results panel. Sent in its own update when the answer's audio
     * starts, which is after the quick-search update above has gone out.
     */
    marketOdds?: import('./MarketOddsCard').MarketOddsCard | null;
    /**
     * #4401 — the host's choice for how a standard room shows these odds,
     * stamped by signaling on the odds update so every client acts alike
     * without holding the setting itself. Absent = `cue`.
     */
    marketOddsDisplay?: import('./MarketOddsCard').MarketOddsDisplay;
    currentTopic: string | null;
    conversationSummary: string | null;
    activitySummary: string | null;
    activityTopic: string | null;
    updatedAt: number;
}

/** Event name constant for talk show topic agenda updates */
export const TALK_SHOW_TOPIC_UPDATE = 'TALK_SHOW_TOPIC_UPDATE';
export const TALK_SHOW_TIMELINE_SYNC = 'TALK_SHOW_TIMELINE_SYNC';
export const TALK_SHOW_TIMELINE_SNAPSHOT = 'TALK_SHOW_TIMELINE_SNAPSHOT';
export const TALK_SHOW_PLAYOUT_SCHEDULE = 'TALK_SHOW_PLAYOUT_SCHEDULE';
export const TALK_SHOW_TOPIC_OVERLAY_SCHEDULE = 'TALK_SHOW_TOPIC_OVERLAY_SCHEDULE';
export const TALK_SHOW_PLAYOUT_STATUS = 'TALK_SHOW_PLAYOUT_STATUS';

/** Payload for TALK_SHOW_TOPIC_UPDATE socket event — pushed to clients when topic agenda changes */
export interface TalkShowTopicUpdatePayload {
    roomId: string;
    agenda: Array<{
        id: string;
        label: string;
        status: 'upcoming' | 'active' | 'covered';
        startedAt?: number;
        completedAt?: number;
    }>;
    activeTopicIndex: number;
    topicSegmentId: number;
    timestamp: number;
}

export interface TalkShowTimelineSyncPayload {
    roomId: string;
    serverNowMs: number;
    roomTimelineMs: number;
    sequence: number;
}

export interface TalkShowTimelineSnapshotPayload {
    roomId: string;
    serverNowMs: number;
    roomTimelineMs: number;
    activeTopicId: string | null;
    activeTopicLabel: string | null;
    activeTopicStartAtTimelineMs: number | null;
    sequence: number;
}

export interface TalkShowPlayoutSchedulePayload {
    roomId: string;
    audioId: string;
    agentId: string;
    startAtTimelineMs: number;
    expectedDurationMs?: number;
    sequence: number;
    yieldId?: string;
    groupPosition?: 'host_ack' | 'caller' | 'post_ack';
    keywordReaction?: TalkShowKeywordReactionPayload;
}

export interface TalkShowKeywordReactionPayload {
    triggerType: 'group' | 'action';
    animationName: string;
    assignments?: Array<{
        companionId: string;
        clipName: string;
    }>;
    reactionClass: 'big' | 'small';
    cameraMode: 'wide_required' | 'wide_preferred' | 'dolly_push_in';
}

export interface TalkShowTopicOverlaySchedulePayload {
    roomId: string;
    topicId: string;
    topicLabel: string;
    startAtTimelineMs: number;
    reason: 'agenda_advance' | 'organic_pivot' | 'manual_admin';
    sequence: number;
}

export interface TalkShowPlayoutStatusPayload {
    roomId: string;
    audioId: string;
    agentId: string;
    status: 'started' | 'ended' | 'seeked';
    clientNowMs: number;
    estimatedTimelineMs: number;
}

// =============================================================================
// Skill command router (Keystone Phase 4 PR-4d)
//
// Four events emitted by the talk_show:submit_contribution handler when the
// transcript matches a wake-word + skill-alias. All four are addressed to
// the submitting socket (host caller), not room-broadcast.
// =============================================================================

/** Event name constants for skill command router outcomes. */
export const TALK_SHOW_SKILL_COMMAND_RESULT = 'talk_show:skill_command_result';
export const TALK_SHOW_SKILL_COMMAND_FAILED = 'talk_show:skill_command_failed';
export const TALK_SHOW_SKILL_COMMAND_DENIED = 'talk_show:skill_command_denied';
export const TALK_SHOW_SKILL_COMMAND_AMBIGUOUS = 'talk_show:skill_command_ambiguous';

/** Emitted when SkillRuntime.invoke returned { ok: true }. */
export interface TalkShowSkillCommandResultPayload {
    roomId: string;
    requestId?: string;
    skillId: string;
    outputText: string;
    finalResponseId: string;
}

/**
 * Emitted when either (a) SkillRuntime.invoke returned { ok: false }, or
 * (b) the wrapper hit a pre-invoke infrastructure error (e.g.
 * NO_ACTIVE_ROOM_SESSION). `code` is either a SkillInvokeErrorCode or a
 * wrapper-specific code; payload deliberately omits `details` (server logs
 * only).
 */
export interface TalkShowSkillCommandFailedPayload {
    roomId: string;
    requestId?: string;
    skillId: string;
    code: string;
    message: string;
}

/** Emitted when the server-side host/auth check refused a matched skill command. */
export interface TalkShowSkillCommandDeniedPayload {
    roomId: string;
    requestId?: string;
    skillId: string;
    reason: string;
}

/** Emitted when two enabled skills declared the same alias and the router refused to pick. */
export interface TalkShowSkillCommandAmbiguousPayload {
    roomId: string;
    requestId?: string;
    alias: string;
    candidates: string[];
}

/** Payload for PROGRAM_COMPANION_METADATA socket event — emitted to program observers on join */
export interface ProgramCompanionMetadataPayload {
    roomId: string;
    companions: Array<{
        sourceId: string;
        companionId: string;
        displayName: string;
        role: string;
        avatarImageUrl: string | null;
        modelPath: string | null;
        framing?: { fov: number; heightOffset: number } | null;
    }>;
    /**
     * #4315 — `companions` is the whole set, not an addition. The join-time emit is additive
     * (an empty list means "nothing known yet"); a live change to what the program should draw
     * needs to be able to remove, so it says so.
     */
    replace?: boolean;
}

/** Recorder state — mirrors useProgramRecorder's RecorderState */
export type ProgramRecorderState = 'idle' | 'buffering' | 'saving' | 'error';

/** Payload for PROGRAM_RECORDER_STATUS — program observer reports recorder state to host */
export interface ProgramRecorderStatusPayload {
    roomId: string;
    state: ProgramRecorderState;
    bufferDurationSeconds: number;
    estimatedSizeMB: number;
    error: string | null;
    timestamp: number;
    /** Whether the program tab is actively WHIP-publishing (drives auto recording mode) */
    isPublishing?: boolean;
}

/** Payload for PROGRAM_RECORDER_SAVE — host commands program observer to save recording */
export interface ProgramRecorderSavePayload {
    roomId: string;
    timestamp: number;
}

export interface CompanionAnimationTriggerPayload {
    roomId: string;
    triggerType: 'group' | 'action';
    animationName: string;
    assignments?: Array<{
        companionId: string;
        clipName: string;
    }>;
    reactionClass?: 'big' | 'small';
    cameraMode?: 'wide_required' | 'wide_preferred' | 'dolly_push_in';
    timestamp: number;
    /**
     * 'targeted': only the companions in `assignments` act; every other companion carries on
     * as it was. Absent: the original talk-show meaning, where the payload is the whole room's
     * new set of reactions. Sent by companion action commands (CompanionActions.ts).
     */
    scope?: 'targeted';
    /**
     * How much of the body the action needs in shot (CompanionActions.ts). Surfaces that frame a
     * bust widen for 'upper'/'full' while it plays. Absent means the shot can stay as it is.
     */
    framing?: 'bust' | 'upper' | 'full';
}

/** Canonical group variant definitions — single source of truth for server + client. */
export const ANIMATION_GROUP_VARIANTS: Record<string, string[]> = {
    angry: ['Angry', 'Angry2', 'Angry3'],
    dance: ['Dance', 'Dance2', 'Dance3', 'Dance4'],
};

enum SocketEvents {
    JOIN_USER_ACCEPTED = 'joined',
    JOIN_USER_REQUEST = 'join',
    JOIN_USER_ALREADY_EXISTS = 'already-exists',
    JOIN_USER_DENIED = 'access-denied',
    ROOM_CREATED = 'created',
    /**
   * #2861 — host toggles Auto-respond: the companion replies to human speech
   * without push-to-talk or a wake phrase. Session-scoped; never persisted, so
   * a room cannot be left listening for whoever opens it next.
   *
   * Applies to HUMAN speech only. Media audio (stage-media, activities) is
   * classified separately (#2859) and never enters the conversation in either
   * mode — the companion must not answer the television whatever the host has
   * chosen.
   */
  /**
   * #2863 — host toggles transcription of desktop/gameplay share audio, so the
   * companion can HEAR a share as well as see it. Session-scoped, and broadcast
   * because the client that owns the share is not necessarily the host who
   * flipped the switch.
   */
  /**
   * #2865 — host toggles frame sampling of stage media (IPTV/HLS, MP4), so the
   * companion can SEE what the room is watching rather than only reading its
   * transcript. YouTube is never sampled: it plays in a cross-origin iframe the
   * page cannot read.
   */
  STAGE_MEDIA_VISION_SET = 'stage-media:vision:set',
  STAGE_MEDIA_VISION_CHANGED = 'stage-media:vision:changed',
  SHARE_AUDIO_TRANSCRIPTION_SET = 'share:audio-transcription:set',
  SHARE_AUDIO_TRANSCRIPTION_CHANGED = 'share:audio-transcription:changed',
  /** #3767 — a share's audio-isolation verdict changed (e.g. contaminated by the room's own voice). Room-wide. */
  SHARE_AUDIO_ISOLATION_CHANGED = 'share:audio-isolation:changed',
  /** #3770 — the sharer's client reports what its acoustic loopback detector found. Host-gated. */
  SHARE_AUDIO_ISOLATION_SET = 'share:audio-isolation:set',
  COMPANION_AUTO_RESPOND_SET = 'companion:auto-respond:set',
  COMPANION_AUTO_RESPOND_CHANGED = 'companion:auto-respond:changed',
  /**
   * #2874 — the three toggles above, served to a socket as it joins.
   *
   * Without this they were session-scoped in name only: the `*_CHANGED` events
   * reach whoever is connected at the moment of the change, so a host who
   * reloaded saw every switch `off` while the companion was still
   * auto-responding, and a participant who joined afterwards never started
   * sampling or transcribing at all.
   *
   * A separate event rather than a replay of the three `*_CHANGED` events,
   * which would announce a change that did not happen. Emitted to the joining
   * socket only — the room already agrees.
   */
  COMPANION_AWARENESS_STATE = 'companion:awareness:state',
  /**
   * #2880 — the host panel asking for the toggle state it displays.
   *
   * `COMPANION_AWARENESS_STATE` above is served when a socket **joins the room**, which fixed
   * joining and reloading but not a component **remount**. Standard rooms render the host panel
   * inside a Radix `Sheet` with no `forceMount`, so closing it destroys the controls and reopening
   * builds them fresh at `false` — the switch then reads off while the companion is still
   * auto-responding. Townhall renders its panel inline and never hit this.
   *
   * Emitted on mount by every control that owns its own socket state. Signaling replies to the
   * requesting socket only, from state it already holds. Also covers reconnects, where the same
   * gap exists.
   */
  HOST_PANEL_STATE_REQUEST = 'host-panel:state:request',
  /**
   * #4401 — the host sets how a standard room shows prediction-market odds
   * (`cue` pill or auto-opened `panel`). Host-gated in signaling. Payload is
   * `MarketOddsDisplayPayload`.
   */
  MARKET_ODDS_DISPLAY_SET = 'market-odds:display:set',
  /**
   * #4401 — the room's odds display changed. Room-wide, sender included; also
   * sent to one socket in reply to `HOST_PANEL_STATE_REQUEST`, so a remounted
   * host panel shows the room's value. Payload is `MarketOddsDisplayPayload`.
   */
  MARKET_ODDS_DISPLAY_CHANGED = 'market-odds:display:changed',
  /**
   * #4315 — the room's companion PIP visibility changed. The value is persisted on the workflow
   * and served in the unified config; this event is only the live push, so a host's flip reaches
   * every client in the room without a reload. Room-wide, sender included. Payload is
   * `CompanionPipChangedPayload`.
   */
  COMPANION_PIP_CHANGED = 'companion:pip:changed',
  ACTIVATE_MODULE = 'activate-module',
    MODULE_ACTIVATED = 'module-activated',
    DEACTIVATE_MODULE = 'deactivate-module',
    MODULE_DEACTIVATED = 'module-deactivated',
    CUSTOM_MODULE_EVENT = 'custom-module-event',
    PEER_RELAY_SIGNAL = 'relay-signal',
    NEW_USER = 'new-participant',
    EXISTING_USERS = 'existing-participants',
    USER_LEFT = 'peer-disconnected',
    ERROR = 'error',
    FATAL_ERROR = 'fatal_error',
    AGENT_STATE_CHANGE = 'agent-state-change',
    AGENT_ENABLED_CHANGE = 'agent-enabled-change',
    AGENT_PLAYBACK_COMPLETE = 'agent-playback-complete',
    AGENT_PLAYBACK_STARTED = 'agent-playback-started',
    /** Renderer diagnostics only; never advances a conversation turn. */
    AGENT_PLAYBACK_OBSERVED = 'agent-playback-observed',
    AGENT_AUDIO_INTERRUPT = 'agent-audio-interrupt',
    AGENT_LLM_TIMEOUT = 'agent-llm-timeout',
    AGENT_REQUEST_FAILED = 'agent-request-failed',
    STT_KEEP_ALIVE = 'stt_keep_alive',
    // Client → stt-api control frame: ends the current streamed utterance.
    // Only meaningful on sessions opened with `streaming=1`, where the relay
    // provider must not auto-finalize per audio write.
    STT_FINALIZE = 'stt_finalize',
    // Client → stt-api control frame: the client's own observation about its capture (#4524 —
    // a push-to-talk press that sent no audio, with the session's state). Logged server-side as
    // [client-diagnostic] so a silent session can be diagnosed from service logs; never
    // interpreted as audio or as a transcript.
    STT_CLIENT_DIAGNOSTIC = 'stt_client_diagnostic',
    FORCED_DISCONNECT = 'force_disconnect',
    ACTIVITY_TRANSCRIPT = 'activity-transcript',
    CLIENT_TRANSCRIPT = 'client-transcript',
    AVATAR_COMPANION_CHANGED = 'avatar-companion-changed',
    AVATAR_COMPANION_UPDATE_ERROR = 'avatar-companion-update-error',
    AVATAR_COMPANION_CONFIG = 'avatar-companion-config',
    /** #3837 client → signaling: `{ roomId, companionId | null }`. */
    PARTICIPANT_PUPPET_CHANGE = 'participant-puppet-change',
    /** #3837 signaling → room, sender included: `ParticipantPuppetChangedPayload`. */
    PARTICIPANT_PUPPET_CHANGED = 'participant-puppet-changed',
    /** #3837 signaling → requesting socket: `ParticipantPuppetErrorPayload`. */
    PARTICIPANT_PUPPET_ERROR = 'participant-puppet-error',
    COMPANION_SWAP = 'companion-swap',
    COMPANION_SWAP_SUCCESS = 'companion-swap-success',
    COMPANION_SWAP_ERROR = 'companion-swap-error',
    COMPANION_ANIMATION_TRIGGER = 'COMPANION_ANIMATION_TRIGGER',
    COMPANION_EMOTION_UPDATE = 'COMPANION_EMOTION_UPDATE',
    ROOM_CONFIGURATION_UPDATED = 'room-configuration-updated',
    PERSONALITY_STREAM_EVENT = 'personality-stream-event',
    PERSONALITY_STREAMING_START = 'personality-streaming-start',
    PERSONALITY_STREAMING_STOP = 'personality-streaming-stop',
    ROOM_CONTEXT_UPDATE = 'room-context-update',
    CANCEL_AMBIENT_SEARCH = 'cancel-ambient-search', // Signal to cancel RoomContextAgent's in-flight search
    PTT_START_TURN ='ptt-start-turn',
    PTT_END_TURN = 'ptt-end-turn',
    // PTT state broadcast to all peers (for UI feedback)
    PTT_STATE_BROADCAST = 'ptt-state-broadcast',
    // Client signals that the companion 3D model is fully loaded and rendered in PIP
    AGENT_MODEL_READY = 'agent-model-ready',
    // Server sends phoneme-driven viseme timeline data for lip sync
    AGENT_VISEME_DATA = 'agent-viseme-data',
    // Broadcast session lifecycle events
    BROADCAST_SESSION_START = 'broadcast:session:start',
    BROADCAST_SESSION_LIVE = 'broadcast:session:live',
    BROADCAST_SESSION_HEARTBEAT = 'broadcast:session:heartbeat',
    BROADCAST_SESSION_STOP = 'broadcast:session:stop',
    BROADCAST_SESSION_ENDED = 'broadcast:session:ended',
    BROADCAST_SESSION_STATUS = 'broadcast:session:status',
    BROADCAST_OUTPUT_STATUS = 'broadcast:output:status',
    // Room layout state (program canvas mirroring)
    ROOM_LAYOUT_STATE = 'room:layout_state',
    // Shared desktop AV source lifecycle
    DESKTOP_AV_SOURCE_STARTED = 'desktop-av:source-started',
    DESKTOP_AV_SOURCE_STOPPED = 'desktop-av:source-stopped',
    // Program Publisher participant WHIP/WHEP media lifecycle
    PARTICIPANT_PROGRAM_MEDIA_READY = 'room:participant_media_ready',
    PARTICIPANT_PROGRAM_MEDIA_STOPPED = 'room:participant_media_stopped',
    PARTICIPANT_PROGRAM_MEDIA_ERROR = 'room:participant_media_error',
    // Host gameplay/screen-share source lifecycle
    GAMEPLAY_SHARE_STARTED = 'gameplay-share:started',
    GAMEPLAY_SHARE_STOPPED = 'gameplay-share:stopped',
    // Shared room vision capability events
    ROOM_VISION_SAMPLING_STATE = 'room:vision_sampling_state',
    PARTICIPANT_CAMERA_CONTROL = 'room:participant_camera_control',
    PARTICIPANT_CAMERA_STATE = 'room:participant_camera_state',
    ROOM_VISION_BURST_REQUESTED = 'room:vision_burst_requested',
    ROOM_VISION_MOMENT_MARKED = 'room:vision_moment_marked',
    // Shared room recording capability events
    ROOM_RECORDING_STATUS = 'room:recording_status',
    ROOM_RECORDING_COMMAND = 'room:recording_command',
    // Program observer: companion metadata for ProgramCanvas rendering
    PROGRAM_COMPANION_METADATA = 'program:companion_metadata',
    // Program recorder: status relay (program observer → server → host)
    PROGRAM_RECORDER_STATUS = 'program:recorder_status',
    // Program recorder: save command relay (host → server → program observer)
    PROGRAM_RECORDER_SAVE = 'program:recorder_save',
    // Sixync canonical room-state stream for Program Publisher
    SIXYNC_SUBSCRIBE = 'sixync:subscribe',
    SIXYNC_SUBSCRIBED = 'sixync:subscribed',
    SIXYNC_EVENT = 'sixync:event',
    SIXYNC_SESSION_CHANGED = 'sixync:session_changed',
    SIXYNC_ERROR = 'sixync:error',
    // Room usage billing lifecycle events
    ROOM_BILLING_WARNING = 'room:billing_warning',
    ROOM_BILLING_ENDED = 'room:billing_ended',
};

export interface SixyncSubscribePayload {
    acceptsUnknownCueKinds?: boolean;
    roomId: string;
    sessionId: string;
    sessionScope?: 'live';
    observerToken: string;
    afterSeq?: number;
}

export interface SixyncSubscribedPayload {
    /** Full repair snapshot, only sent to capability-advertising readers. */
    snapshot?: SixyncSnapshot;
    roomId: string;
    sessionId: string;
    requestedSessionId?: string;
    sessionScope?: 'live';
    resolvedSessionId?: string;
    sessionGeneration?: string;
    socketRoom: string;
    snapshotVersion: number;
    sceneVersion: number;
    latestSeq: number;
    requestedAfterSeq: number;
    activeSourceIds: string[];
}

export interface SixyncEventPayload {
    roomId: string;
    sessionId: string;
    event: SixyncEvent;
    snapshotVersion: number;
    sceneVersion: number;
    serverTime: number;
}

export interface SixyncSessionChangedPayload {
    roomId: string;
    sessionScope: 'live';
    status: 'active' | 'waiting_for_live_session';
    requestedSessionId?: string;
    resolvedSessionId?: string;
    sessionGeneration?: string;
    reason?: string;
}

export interface SixyncSocketErrorPayload {
    roomId?: string;
    sessionId?: string;
    sessionScope?: 'live';
    resolvedSessionId?: string;
    code: 'validation' | 'authorization_failed' | 'not_found' | 'waiting_for_live_session' | 'internal_error';
    message: string;
}

export type RoomBillingSocketTier = 'base' | 'pro';

export interface RoomBillingWarningPayload {
    roomId: string;
    roomType: string;
    tier: RoomBillingSocketTier;
    rateMultiplier: number;
    remainingSeconds: number;
    warningThresholdSeconds: number;
    enforcementMode: 'warn' | 'enforce';
    message: string;
}

export interface RoomBillingEndedPayload {
    roomId: string;
    roomType: string;
    tier: RoomBillingSocketTier;
    rateMultiplier: number;
    remainingSeconds: number;
    reason: 'credits_exhausted';
    message: string;
}

export interface RoomBillingJoinDeniedPayload {
    roomId: string;
    roomType?: string;
    tier?: RoomBillingSocketTier;
    rateMultiplier?: number;
    remainingSeconds?: number;
    reason: 'credits_exhausted' | 'missing_host' | 'room_not_found' | 'access_denied';
    code: 'ROOM_BILLING_CREDITS_EXHAUSTED' | 'ROOM_BILLING_MISSING_HOST' | 'ROOM_ACCESS_DENIED';
    message: string;
}

enum SimplePeerEvents {
    PEER_CONNECTED = 'peer:connected',
    PEER_DISCONNECTED = 'peer:disconnected',
    PEER_STREAM_UPDATED = 'peer:stream:updated',
    PEER_REMOVED = 'peer:removed',
    PEER_TRACK_ENDED = 'peer:track:ended',
    PEER_TRACK_MUTED = 'peer:track:muted',
    PEER_TRACK_UNMUTED = 'peer:track:unmuted',
    PEER_MEDIA_STATE_UPDATED = 'peer:media:state:updated'
}

/**
 * YouTube/Playlist Socket Event Types
 * Per Streams Sheet Redesign Functional Spec Section 3.3
 */

/**
 * YouTube search request event
 */
export interface YouTubeSearchRequestEvent {
    roomId: string;
    query: string;
    sessionId: string;
}

/**
 * YouTube search result item
 */
export interface YouTubeSearchResultItem {
    videoId: string;
    title: string;
    thumbnailUrl: string;
    channel?: string;
    duration?: string;
    url: string;
}

/**
 * YouTube search response event
 */
export interface YouTubeSearchResponseEvent {
    roomId: string;
    query: string;
    results: YouTubeSearchResultItem[];
    error?: string;
}

/**
 * Playlist item structure
 */
export interface PlaylistItem {
    videoId: string;
    title: string;
    thumbnailUrl: string;
    channel?: string;
    duration?: string;
    url: string;
    addedBy: string;
    addedAt: number;
}

/**
 * Playlist action types
 */
export type PlaylistAction = 'add' | 'remove' | 'clear' | 'play' | 'advance' | 'sync';

/**
 * Playlist update request event (client -> server)
 */
export interface PlaylistUpdateRequestEvent {
    revision?: number;
    generation?: string;
    expectedVideoId?: string;
    roomId: string;
    action: PlaylistAction;
    item?: PlaylistItem;
    index?: number;
    sessionId: string;
}

/**
 * Playlist update broadcast event (server -> clients)
 */
export interface PlaylistUpdateBroadcastEvent {
    revision?: number;
    generation?: string;
    playbackId?: string | null;
    roomId: string;
    playlist: PlaylistItem[];
    currentIndex: number;
    action: PlaylistAction;
    updatedBy?: string;
}

/**
 * Playlist state request event
 */
export interface PlaylistRequestEvent {
    roomId: string;
}

/**
 * Custom module event for YouTube-related events
 */
export interface YouTubeModuleEvent {
    roomId: string;
    moduleId: string;
    eventName: 'youtube-video-ended' | 'clear-youtube-playlist';
    data?: {
        playlistGeneration?: string;
        playlistPlaybackId?: string;
        sessionId?: string;
        videoId?: string;
    };
}

/**
 * Layout modes for the room's visual state.
 * ProgramCanvas maps these to canvas-specific rendering layouts.
 */
export type RoomLayoutMode = 'pip' | 'promoted' | 'fullscreen' | 'brady_bunch' | 'stage_grid';

/**
 * Stage Grid (#3811) — what the host has on the stage, so a program observer
 * can run the same layout engine at its own size and draw the same
 * composition. `media` is anything 16:9 (a share, a stream, a video, an
 * activity that is one); `card` is the interactive overlay's slot;
 * `scoreBug` / `chip` are the ambient status pair.
 */
export type RoomLayoutSurfaceKind = 'media' | 'card' | 'scoreBug' | 'chip';

export interface RoomLayoutSurface {
    kind: RoomLayoutSurfaceKind;
    /** Stable id: 'media:townhall-stage', 'card:poll', 'card:presentation', … */
    id: string;
    /** For `media`: the content aspect, for letterboxing. Default 16:9. */
    aspect?: number;
}

/** PIP dock edge position — matches OrientationPIPDocks portrait/landscape placement. */
export type PipDockPosition = 'left' | 'right' | 'top' | 'bottom';

/**
 * Room layout state broadcast to program observers for canvas mirroring.
 * Emitted by RoomManager whenever the visual layout changes.
 */
export interface RoomLayoutStatePayload {
  roomId: string;
  /** Current layout mode */
  mode: RoomLayoutMode;
  /** Session ID of the promoted/fullscreen participant (null in 'pip' or 'brady_bunch' modes) */
  activeParticipantId: string | null;
  /** Ordered session IDs in the PIP dock (excludes the promoted participant) */
  pipParticipantIds: string[];
  /** All participant session IDs currently in the room (for grid fallback) */
  allParticipantIds: string[];
  /** Which edge the PIP dock is on (derived from device orientation) */
  pipDockPosition: PipDockPosition;
  /** Timestamp for ordering/dedup */
  timestamp: number;
  /** Maps agent session IDs (random UUIDs from ParticipantManager) to companion IDs (DB UUIDs).
   *  Allows ProgramCanvas to translate layout participant IDs to companion-keyed agent IDs.
   *  Optional for backward compatibility — older hosts omit this field. */
  companionSessionMap?: Record<string, string>;
  /** PIP size preference from the host — ProgramCanvas scales PIP thumbnails accordingly.
   *  Optional for backward compatibility — older hosts omit this field (defaults to 'small'). */
  pipSize?: 'extraSmall' | 'small' | 'medium' | 'large' | 'extraLarge';
  /** Host-program intent used by the native compositor to choose a semantic layout template. */
  hostExperienceMode?: HostExperienceMode;
  /** Stage Grid (#3811): the host's surface inventory when `mode` is `'stage_grid'`.
   *  Optional — older hosts omit it and older observers ignore it. */
  surfaces?: RoomLayoutSurface[];
}

// ─── Shared stage media (capability: supportsSharedStageMedia) ──────────────

/** Server → clients: full stage-media state snapshot after any mutation. */
export const STAGE_MEDIA_STATE_UPDATE = 'stage-media:state';

/** Clients → server: participant-initiated transport op (play/pause/seek). */
export const STAGE_MEDIA_CONTROL = 'stage-media:control';

/** Server → clients: relative transport jump (±Ns) that does not alter canonical
 *  position for HLS (live) but shifts every viewer's local `<video>.currentTime`
 *  by the same delta. For MP4 the server also updates canonical positionSec so
 *  late-joiners snap correctly. Carries a nonce so clients can dedup across
 *  redeliveries / state-update races. */
export const STAGE_MEDIA_SEEK_RELATIVE = 'stage-media:seek-relative';

export interface StageMediaStateUpdatePayload {
    roomId: string;
    /** Full state — matches StageMediaState in @telegamez/types. Intentionally
     *  a structural duplicate to keep WebSocketEvents free of StageMedia imports
     *  (those types already re-exported from the root). */
    state: import('./StageMedia').StageMediaState;
    /** Server timestamp for ordering/dedup across nodes. */
    timestamp: number;
}

export interface StageMediaControlPayload {
    roomId: string;
    /** Transport op requested by a stage participant. */
    op:
        | { type: 'play' }
        | { type: 'pause' }
        | { type: 'seek'; positionSec: number };
}

export interface StageMediaSeekRelativePayload {
    roomId: string;
    /** Seconds to shift each client's local playback position. Negative = rewind. */
    deltaSec: number;
    /** Kind the seek was requested against — clients use this to guard against
     *  stale events that arrive after a source switch. */
    kind: import('./StageMedia').StageMediaKind;
    /** Monotonic id so clients can dedup if the event is redelivered. */
    nonce: string;
    /** Server wall-clock for diagnostics / drift measurement. */
    timestamp: number;
}

export { SocketEvents, SimplePeerEvents };
